import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Flame,
  AlertTriangle,
  Mountain,
  Users,
  Heart,
  Plus,
  Image as ImageIcon,
  ShieldCheck,
} from 'lucide-react';
import { PageShell } from '@/components/primitives/PageShell';
import { PageHeader } from '@/components/primitives/PageHeader';
import { Section } from '@/components/primitives/Section';
import { StatCard } from '@/components/primitives/StatCard';
import { EmptyState } from '@/components/primitives/EmptyState';
import { RoleGuard } from '@/components/primitives/RoleGuard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { getStatsSummary, listWildfires, listWildfireSeasons } from '@/api/wildfires';
import { listPendingImages } from '@/api/images';
import { listUsers } from '@/api/users';
import { cn } from '@/lib/utils';

interface StatsSummary {
  total_wildfires?: number | null;
  total_active_wildfires?: number | null;
  total_area_burnt?: number | null;
  total_man_hours?: number | null;
  total_injuries?: number | null;
}

interface Wildfire {
  id: number;
  fire_reference?: string | null;
  start_time?: string | null;
  suburb?: string | null;
  address?: string | null;
  area_size_hectares?: number | null;
  is_active?: boolean;
}

interface WildfireListResponse {
  wildfires: Wildfire[];
  total: number;
}

interface PaginatedCount {
  total?: number;
}

interface SeasonsResponse {
  current_season?: string | null;
}

function formatNumber(n: number | null | undefined): string {
  if (n == null) return '0';
  return Number(n).toLocaleString();
}

export default function Dashboard() {
  const { currentUser, isStaff, isAdmin } = useAuth();
  const { tenantId } = useTenant();

  const [stats, setStats] = useState<StatsSummary | null>(null);
  const [season, setSeason] = useState<string | null>(null);
  const [recent, setRecent] = useState<Wildfire[]>([]);
  const [pendingImages, setPendingImages] = useState<number>(0);
  const [pendingUsers, setPendingUsers] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const baseParams: Record<string, unknown> = {};
        if (tenantId) baseParams.tenant_id = tenantId;

        // Resolve the current fire season so the headline stats reflect this
        // season only, rather than aggregating every fire ever recorded.
        const seasonsData = (await listWildfireSeasons(baseParams)) as SeasonsResponse;
        if (cancelled) return;
        const currentSeason = seasonsData?.current_season ?? null;
        setSeason(currentSeason);

        const statsParams = { ...baseParams };
        if (currentSeason) statsParams.fire_season = currentSeason;
        const statsData = (await getStatsSummary(statsParams)) as StatsSummary;
        if (cancelled) return;
        setStats(statsData);

        const wildfiresData = (await listWildfires({
          ...baseParams,
          page: 1,
          page_size: 5,
        })) as unknown as WildfireListResponse;
        if (cancelled) return;
        setRecent(wildfiresData.wildfires || []);

        if (isStaff()) {
          try {
            const imgData = (await listPendingImages({
              page: 1,
              page_size: 1,
            })) as PaginatedCount;
            if (!cancelled) setPendingImages(imgData.total ?? 0);
          } catch (err) {
            console.warn('Could not fetch pending image count:', err);
          }

          if (isAdmin()) {
            try {
              const userData = (await listUsers({
                is_approved: false,
                page: 1,
                page_size: 1,
              })) as PaginatedCount;
              if (!cancelled) setPendingUsers(userData.total ?? 0);
            } catch (err) {
              console.warn('Could not fetch pending user count:', err);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        if (!cancelled) setError('Failed to load dashboard data. Please try again later.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const greetingName = currentUser?.display_name || currentUser?.username || '';

  return (
    <PageShell>
      <PageHeader
        title="Dashboard"
        subtitle={greetingName ? `Welcome back, ${greetingName}` : 'Welcome back'}
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <RoleGuard staff>
        <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Button asChild size="lg" className="h-12 justify-start gap-2">
            <Link to="/wildfires/new">
              <Plus className="h-5 w-5" />
              Report wildfire
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="h-12 justify-start gap-2">
            <Link to="/inbox?tab=images">
              <ImageIcon className="h-5 w-5" />
              Moderate content
              {pendingImages > 0 && (
                <Badge variant="destructive" className="ml-auto">
                  {pendingImages}
                </Badge>
              )}
            </Link>
          </Button>
          <RoleGuard admin>
            <Button asChild size="lg" variant="outline" className="h-12 justify-start gap-2">
              <Link to="/inbox?tab=users">
                <ShieldCheck className="h-5 w-5" />
                Approve users
                {pendingUsers > 0 && (
                  <Badge variant="destructive" className="ml-auto">
                    {pendingUsers}
                  </Badge>
                )}
              </Link>
            </Button>
          </RoleGuard>
        </div>
      </RoleGuard>

      <div className="mb-2 flex items-baseline justify-between">
        <h2 className="text-sm font-medium text-muted-foreground">
          {season ? `Current season · ${season}` : 'Current season'}
        </h2>
      </div>

      <div className="mb-6 grid gap-3 grid-cols-2 lg:grid-cols-5">
        <StatCard
          icon={Flame}
          tone="primary"
          label="Total wildfires"
          value={loading ? '' : formatNumber(stats?.total_wildfires)}
          loading={loading}
        />
        <StatCard
          icon={AlertTriangle}
          tone="destructive"
          label="Active now"
          value={loading ? '' : formatNumber(stats?.total_active_wildfires)}
          loading={loading}
        />
        <StatCard
          icon={Mountain}
          tone="secondary"
          label="Area burnt (ha)"
          value={loading ? '' : formatNumber(stats?.total_area_burnt)}
          loading={loading}
        />
        <StatCard
          icon={Users}
          tone="default"
          label="Man hours"
          value={loading ? '' : formatNumber(stats?.total_man_hours)}
          loading={loading}
        />
        <StatCard
          icon={Heart}
          tone="default"
          label="Injuries"
          value={loading ? '' : formatNumber(stats?.total_injuries)}
          loading={loading}
        />
      </div>

      <Section
        title="Recent wildfires"
        actions={
          <Button asChild variant="ghost" size="sm">
            <Link to="/wildfires">View all →</Link>
          </Button>
        }
        unpadded
      >
        {loading ? (
          <div className="divide-y divide-border">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-16 animate-pulse bg-muted/30" />
            ))}
          </div>
        ) : recent.length === 0 ? (
          <EmptyState
            icon={Flame}
            title="No recent wildfires reported"
            description="When wildfires are reported they will appear here."
            action={
              <RoleGuard staff>
                <Button asChild>
                  <Link to="/wildfires/new">
                    <Plus className="mr-2 h-4 w-4" />
                    Report wildfire
                  </Link>
                </Button>
              </RoleGuard>
            }
          />
        ) : (
          <ul className="divide-y divide-border">
            {recent.map((w) => (
              <li key={w.id}>
                <Link
                  to={`/wildfires/${w.id}`}
                  className="flex items-center gap-3 px-4 py-3 transition hover:bg-muted/50"
                >
                  <Flame
                    className={cn(
                      'h-5 w-5 shrink-0',
                      w.is_active ? 'text-destructive' : 'text-muted-foreground',
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-mono text-sm font-semibold">
                        {w.fire_reference || `#${w.id}`}
                      </span>
                      {w.is_active && (
                        <Badge variant="destructive" className="text-xs">
                          Active
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm text-muted-foreground">
                        {w.suburb || 'Unknown suburb'}
                      </span>
                      {w.area_size_hectares != null && (
                        <span className="shrink-0 text-sm text-muted-foreground">
                          {formatNumber(w.area_size_hectares)} ha
                        </span>
                      )}
                    </div>
                    {w.address && (
                      <div className="truncate text-xs text-muted-foreground">
                        {w.address}
                      </div>
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </PageShell>
  );
}

